/**
 * useBreakpoint — single source of truth for phone/tablet/desktop layout
 * decisions across the app. Returns one of: 'phone' | 'tablet' | 'desktop'.
 *
 * Thresholds from the phone/tablet UX blueprint (issue #704):
 *   phone   ≤ 767px  (iPhone portrait/landscape, small Androids)
 *   tablet  768-1199px (iPad portrait/landscape + narrow laptop widths)
 *   desktop ≥ 1200px (wide laptops, monitors)
 *
 * Subscribes to window 'resize' so a rotation or external window-resize
 * triggers a re-render. SSR-safe via the `typeof window` guard inside
 * detect(). Future PRs (PR-2 BottomTabBar, PR-5 iPad sidebar) consume
 * this hook directly rather than re-doing matchMedia logic per component.
 */
import { useEffect, useState } from 'react';

const PHONE_MAX = 767;
const TABLET_MAX = 1199;

export function useBreakpoint() {
  const [bp, setBp] = useState(() => detect());

  useEffect(() => {
    const onResize = () => setBp(detect());
    window.addEventListener('resize', onResize);
    // Recompute once on mount in case the initial server-side render
    // returned the 'desktop' fallback before window was available.
    setBp(detect());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return bp;
}

function detect() {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w <= PHONE_MAX) return 'phone';
  if (w <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

export default useBreakpoint;
